import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { useHookstate } from '@hookstate/core';
import { getAllEquipmentGroups } from '~/features/Resources/warehouse/equipmentGroups/services';
import IEquipmentGroup from '~/features/Resources/warehouse/equipmentGroups/interfaces/IEquipmentGroup';

interface FilterAutocompleteEquipmentGroupProps {
  readonly onEquipmentGroupChange: (value: number | null) => void;
  readonly equipmentGroupId?: number | null;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly isRequired?: boolean;
}

export default function FilterAutocompleteEquipmentGroup({
  disabled,
  onEquipmentGroupChange,
  equipmentGroupId,
  error = false,
  isRequired = false,
}: FilterAutocompleteEquipmentGroupProps) {
  const { translate } = useLang();
  const [options, setOptions] = useState<readonly IEquipmentGroup[]>([]);
  const [value, setValue] = useState<IEquipmentGroup | null>(null);
  const loading = useHookstate(false);

  useEffect(() => {
    loading.set(true);
    getAllEquipmentGroups().then((equipmentGroups) => {
      setOptions(equipmentGroups);
      loading.set(false);
    });
  }, []);

  useEffect(() => {
    if (!equipmentGroupId) {
      setValue(null);
    } else {
      const option = options.find((option) => option.id === equipmentGroupId);
      setValue(option ?? null);
    }
  }, [options, equipmentGroupId]);

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        id="filter-equipment-group"
        options={options}
        disabled={disabled}
        value={value}
        noOptionsText={translate('No options')}
        size="small"
        loading={loading.get()}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name ?? ''}
        onChange={(_, equipmentGroup) => {
          setValue(equipmentGroup);
          onEquipmentGroupChange(equipmentGroup?.id ?? null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${translate('Equipment Group')}${isRequired ? ' *' : ''}`}
            error={error}
          />
        )}
        clearText={translate('Clear')}
      />
    </FormControl>
  );
}
